import React, { useState } from "react";
import { useHistory } from "react-router-dom";

import { trackEvent, NotificationStatuses } from "@jsainsburyplc/id-ui-common";

import useCookies from "hooks/useCookies";
import { getInitiateRegistrationLink, getRegistrationLink } from "utils/urls";
import { getErrorMessage } from "utils/otpForm";
import { registrationService } from "services/registration.service";
import { actionTypes, categoryTypes } from "constants/tracking";
import { newCodeMessage, newCodeErrorMessage } from "constants/mfaAndOtp";

import RouterLink from "components/RouterLink";
import PageFormat from "components/PageFormat";
import OtpForm from "components/OtpForm";
import useDigitalDataTracking from "hooks/useDigitalTrack";

const trackRequestCode = (action) => {
  trackEvent({
    category: categoryTypes.REGISTRATION,
    label: "Request new OTP code",
    action,
  });
};

const trackFormSubmit = (action, code = null) => {
  trackEvent({
    category: categoryTypes.REGISTRATION,
    action,
    label: code ? `Submit OTP code: ${code}` : "Submit OTP code",
  });
};

const RegistrationOtpPage = () => {
  const { email } = useCookies();
  const [requestingNewCode, setRequestingNewCode] = useState(false);
  const [message, setMessage] = useState(undefined);

  const history = useHistory();
  useDigitalDataTracking(
    "web:groceries:authentication:register:verify email",
    "authentication",
    "authRegisterVerifyEmail",
    "view"
  );
  const submitHandler = async ({ code }, { setSubmitting }) => {
    setSubmitting(true);
    setMessage(undefined);
    trackFormSubmit(actionTypes.SUBMIT);
    try {
      const response = await registrationService.validateOtp(code, email);
      if (response.ok) {
        const { token } = await response.json();
        history.push(getRegistrationLink(token));
        trackFormSubmit(actionTypes.SUCCESS);
      } else {
        const error = await response.json();
        throw error.code;
      }
    } catch (error) {
      trackFormSubmit(actionTypes.ERROR, error);
      setSubmitting(false);
      setMessage(getErrorMessage(error));
    }
  };

  const handleRequestNewCode = async (event) => {
    event && event.preventDefault();
    trackRequestCode(actionTypes.CODE_REQUESTED);
    setRequestingNewCode(true);

    try {
      const response = await registrationService.requestOtp(email);
      if (response.ok) {
        trackRequestCode(actionTypes.SUCCESS);
        setRequestingNewCode(false);
        setMessage({
          type: NotificationStatuses.SUCCESS_TYPE,
          text: newCodeMessage,
        });
      } else {
        throw Error;
      }
    } catch (error) {
      trackRequestCode(actionTypes.ERROR);
      setRequestingNewCode(false);
      setMessage({
        type: NotificationStatuses.ERROR_TYPE,
        text: newCodeErrorMessage,
      });
    }
  };

  return (
    <PageFormat>
      <div className="ln-u-margin-top*4">
        <h1 className="ln-u-h3">We need to verify your email address</h1>

        <div className="ln-u-push-top-sm">
          <p>
            Before you can finish registering with Sainsbury's, we need to
            verify your email address. We've sent you a code to{" "}
            <span className="ln-u-font-weight-bold">{email}</span>.
          </p>
          <p>Please enter the code to continue.</p>
        </div>
        <OtpForm
          onRequestCode={handleRequestNewCode}
          onSubmit={submitHandler}
          isRequestingCode={requestingNewCode}
          notification={message}
          hideTroubleMessage
        />
        <p className="ln-u-margin-top*4">
          Is this the wrong email address? To register with a new email, you can{" "}
          <RouterLink
            className="ln-c-link"
            link={getInitiateRegistrationLink()}
          >
            go back and start again
          </RouterLink>
          .
        </p>
      </div>
    </PageFormat>
  );
};

export default RegistrationOtpPage;
